(() => {
  const initialize = (root) => {
    if (!root || root.dataset.lcsReady === 'true') return;
    root.dataset.lcsReady = 'true';

    const cleanup = [];
    const listen = (target, eventName, handler, options) => {
      if (!target) return;
      target.addEventListener(eventName, handler, options);
      cleanup.push(() => target.removeEventListener(eventName, handler, options));
    };

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const slides = [...root.querySelectorAll('[data-lcs-slide]')];
    const heroDots = [...root.querySelectorAll('[data-lcs-hero-dot]')];
    const previousHeroButton = root.querySelector('[data-lcs-hero-previous]');
    const nextHeroButton = root.querySelector('[data-lcs-hero-next]');
    const hero = root.querySelector('[data-lcs-hero]');
    let currentSlide = Math.max(slides.findIndex((slide) => slide.classList.contains('is-active')), 0);
    let autoplayTimer;

    const setSlide = (nextIndex) => {
      if (!slides.length) return;
      currentSlide = (nextIndex + slides.length) % slides.length;

      slides.forEach((slide, index) => {
        const isCurrent = index === currentSlide;
        slide.classList.toggle('is-active', isCurrent);
        slide.toggleAttribute('inert', !isCurrent);
        slide.setAttribute('aria-hidden', String(!isCurrent));
      });

      heroDots.forEach((dot, index) => dot.toggleAttribute('aria-current', index === currentSlide));
    };

    const stopAutoplay = () => window.clearInterval(autoplayTimer);
    const startAutoplay = () => {
      stopAutoplay();
      if (reduceMotion.matches || slides.length < 2 || hero?.dataset.autoplay !== 'true') return;
      const interval = Math.max(Number(hero.dataset.interval || 7), 3) * 1000;
      autoplayTimer = window.setInterval(() => setSlide(currentSlide + 1), interval);
    };

    heroDots.forEach((dot, index) => listen(dot, 'click', () => {
      setSlide(index);
      startAutoplay();
    }));
    listen(previousHeroButton, 'click', () => {
      setSlide(currentSlide - 1);
      startAutoplay();
    });
    listen(nextHeroButton, 'click', () => {
      setSlide(currentSlide + 1);
      startAutoplay();
    });
    listen(hero, 'mouseenter', stopAutoplay);
    listen(hero, 'mouseleave', startAutoplay);
    listen(hero, 'focusin', stopAutoplay);
    listen(hero, 'focusout', startAutoplay);
    listen(document, 'visibilitychange', () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });
    startAutoplay();

    const gallery = root.querySelector('[data-lcs-gallery]');
    const galleryRail = gallery?.querySelector('[data-lcs-gallery-rail]');
    const galleryPrevious = gallery?.querySelector('[data-lcs-gallery-previous]');
    const galleryNext = gallery?.querySelector('[data-lcs-gallery-next]');
    const galleryProgress = gallery?.querySelector('[data-lcs-gallery-progress]');

    const updateGallery = () => {
      if (!galleryRail) return;
      const maximumScroll = Math.max(galleryRail.scrollWidth - galleryRail.clientWidth, 0);
      const scrollRatio = maximumScroll > 0 ? galleryRail.scrollLeft / maximumScroll : 0;
      if (galleryPrevious) galleryPrevious.disabled = galleryRail.scrollLeft <= 2;
      if (galleryNext) galleryNext.disabled = galleryRail.scrollLeft >= maximumScroll - 2;

      if (galleryProgress) {
        const thumbWidth = maximumScroll > 0 ? Math.max((galleryRail.clientWidth / galleryRail.scrollWidth) * 100, 18) : 100;
        const travel = 100 - thumbWidth;
        galleryProgress.style.width = `${thumbWidth}%`;
        galleryProgress.style.transform = `translateX(${thumbWidth > 0 ? (scrollRatio * travel * 100) / thumbWidth : 0}%)`;
      }
    };

    const scrollGallery = (direction) => {
      const card = galleryRail?.querySelector('[data-lcs-gallery-card]');
      if (!galleryRail || !card) return;
      const styles = window.getComputedStyle(galleryRail);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || 0);
      galleryRail.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    };

    listen(galleryPrevious, 'click', () => scrollGallery(-1));
    listen(galleryNext, 'click', () => scrollGallery(1));
    listen(galleryRail, 'scroll', updateGallery, { passive: true });
    listen(window, 'resize', updateGallery);
    window.requestAnimationFrame(updateGallery);

    const categoryButtons = [...root.querySelectorAll('[data-lcs-category]')];
    const intentionSelect = root.querySelector('[data-lcs-intention]');
    const intentionControl = root.querySelector('[data-lcs-intention-control]');
    const intentionTrigger = root.querySelector('[data-lcs-intention-trigger]');
    const intentionLabel = root.querySelector('[data-lcs-intention-label]');
    const intentionOptions = root.querySelector('[data-lcs-intention-options]');
    const intentionOptionButtons = [...root.querySelectorAll('[data-lcs-intention-option]')];
    const productGrid = root.querySelector('[data-lcs-product-grid]');
    const emptyState = root.querySelector('[data-lcs-empty]');
    const loadingState = root.querySelector('[data-lcs-loading]');
    let productCards = [...root.querySelectorAll('[data-lcs-product]')];
    let selectedCategory = 'all';

    const normalize = (value) => String(value || '').trim().toLowerCase();
    const hasDelimitedValue = (haystack, needle) => {
      const normalizedNeedle = normalize(needle);
      if (!normalizedNeedle) return false;
      return String(haystack || '').split('|').some((value) => normalize(value) === normalizedNeedle);
    };

    const categoryValues = categoryButtons
      .map((button) => normalize(button.dataset.lcsCategory))
      .filter((value) => value && value !== 'all' && value !== 'other');
    const categoryNeedles = (value) => {
      const normalizedValue = normalize(value);
      const singularValue = normalizedValue.endsWith('s') ? normalizedValue.slice(0, -1) : normalizedValue;
      return [...new Set([normalizedValue, singularValue].filter(Boolean))];
    };
    const matchesCategory = (searchableText, value) => categoryNeedles(value).some((needle) => searchableText.includes(needle));

    const filterProducts = () => {
      const selectedIntention = normalize(intentionSelect?.value || 'all');
      let visibleCount = 0;

      productCards.forEach((card) => {
        const productType = normalize(card.dataset.productType);
        const tags = card.dataset.productTags || '';
        const intentions = card.dataset.productIntentions || tags;
        const searchableCategoryText = normalize(`${productType}|${tags}|${card.textContent}`);
        const searchableIntentionText = normalize(`${intentions}|${tags}|${card.textContent}`);
        const categoryMatches = selectedCategory === 'all'
          || (selectedCategory === 'other'
            ? !categoryValues.some((value) => matchesCategory(searchableCategoryText, value))
            : productType === selectedCategory
              || hasDelimitedValue(tags, selectedCategory)
              || matchesCategory(searchableCategoryText, selectedCategory));
        const intentionMatches = selectedIntention === 'all'
          || hasDelimitedValue(intentions, selectedIntention)
          || hasDelimitedValue(tags, selectedIntention)
          || searchableIntentionText.includes(selectedIntention);
        const shouldShow = categoryMatches && intentionMatches;
        card.hidden = !shouldShow;
        if (shouldShow) visibleCount += 1;
      });

      if (emptyState) emptyState.hidden = visibleCount > 0 || productCards.length === 0;
    };

    categoryButtons.forEach((button) => listen(button, 'click', () => {
      selectedCategory = normalize(button.dataset.lcsCategory) || 'all';
      categoryButtons.forEach((candidate) => {
        const isActive = candidate === button;
        candidate.classList.toggle('is-active', isActive);
        candidate.setAttribute('aria-pressed', String(isActive));
      });
      filterProducts();
    }));

    const closeIntentionOptions = () => {
      if (!intentionOptions || !intentionTrigger) return;
      intentionOptions.hidden = true;
      intentionTrigger.setAttribute('aria-expanded', 'false');
    };

    const openIntentionOptions = (focusSelected = false) => {
      if (!intentionOptions || !intentionTrigger) return;
      intentionOptions.hidden = false;
      intentionTrigger.setAttribute('aria-expanded', 'true');
      if (focusSelected) {
        const selected = intentionOptionButtons.find((option) => option.classList.contains('is-selected')) || intentionOptionButtons[0];
        selected?.focus();
      }
    };

    const selectIntention = (value, label) => {
      const normalizedValue = normalize(value) || 'all';
      if (intentionSelect) intentionSelect.value = normalizedValue;
      if (intentionLabel) intentionLabel.textContent = label;
      intentionOptionButtons.forEach((option) => {
        const isSelected = normalize(option.dataset.lcsIntentionOption) === normalizedValue;
        option.classList.toggle('is-selected', isSelected);
        option.setAttribute('aria-selected', String(isSelected));
      });
      closeIntentionOptions();
      filterProducts();
    };

    listen(intentionTrigger, 'click', () => {
      if (intentionTrigger.getAttribute('aria-expanded') === 'true') closeIntentionOptions();
      else openIntentionOptions();
    });

    listen(intentionTrigger, 'keydown', (event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      openIntentionOptions(true);
    });

    intentionOptionButtons.forEach((option, index) => {
      listen(option, 'click', () => selectIntention(option.dataset.lcsIntentionOption, option.textContent.trim()));
      listen(option, 'keydown', (event) => {
        let nextIndex = index;
        if (event.key === 'ArrowDown') nextIndex = (index + 1) % intentionOptionButtons.length;
        else if (event.key === 'ArrowUp') nextIndex = (index - 1 + intentionOptionButtons.length) % intentionOptionButtons.length;
        else if (event.key === 'Escape') {
          closeIntentionOptions();
          intentionTrigger?.focus();
          return;
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          selectIntention(option.dataset.lcsIntentionOption, option.textContent.trim());
          intentionTrigger?.focus();
          return;
        } else return;
        event.preventDefault();
        intentionOptionButtons[nextIndex]?.focus();
      });
    });

    listen(intentionSelect, 'change', () => {
      const selectedOption = intentionOptionButtons.find((option) => normalize(option.dataset.lcsIntentionOption) === normalize(intentionSelect.value));
      selectIntention(intentionSelect.value, selectedOption?.textContent.trim() || intentionSelect.options[intentionSelect.selectedIndex]?.text || '');
    });

    listen(document, 'click', (event) => {
      if (intentionControl && !intentionControl.contains(event.target)) closeIntentionOptions();
    });

    const loadRemainingProducts = async () => {
      let nextUrl = root.querySelector('[data-lcs-next-page]')?.dataset.lcsNextPage;
      if (!nextUrl || !productGrid) return;

      try {
        while (nextUrl) {
          const url = new URL(nextUrl, window.location.origin);
          url.searchParams.set('section_id', root.dataset.lcsRoot);
          const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!response.ok) throw new Error(`Unable to load products (${response.status})`);
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const nextRoot = parsed.querySelector('[data-lcs-root]');
          if (!nextRoot) break;

          const newCards = [...nextRoot.querySelectorAll('[data-lcs-product]')];
          newCards.forEach((card) => productGrid.append(card));
          productCards = [...root.querySelectorAll('[data-lcs-product]')];
          nextUrl = nextRoot.querySelector('[data-lcs-next-page]')?.dataset.lcsNextPage || '';
        }
      } catch (error) {
        console.warn('[Lotus Crystal] Product loading stopped.', error);
      } finally {
        if (loadingState) loadingState.hidden = true;
        filterProducts();
      }
    };

    loadRemainingProducts();
    filterProducts();

    const revealItems = [...root.querySelectorAll('[data-lcs-reveal]')];
    if (!reduceMotion.matches && 'IntersectionObserver' in window && revealItems.length) {
      revealItems.forEach((item, index) => item.style.setProperty('--lcs-reveal-delay', `${Math.min(index % 4, 3) * 70}ms`));
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
      revealItems.forEach((item) => observer.observe(item));
      cleanup.push(() => observer.disconnect());
    } else {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    }

    root.lcsDestroy = () => {
      stopAutoplay();
      cleanup.forEach((callback) => callback());
      delete root.lcsDestroy;
      delete root.dataset.lcsReady;
    };
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.('[data-lcs-root]')) initialize(scope);
    scope.querySelectorAll?.('[data-lcs-root]').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-lcs-root]').forEach((root) => root.lcsDestroy?.());
  });
})();
